"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Wrench,
  Bolt,
  Hammer,
  Snowflake,
  Ruler,
  Paintbrush,
  Home,
  TreeDeciduous,
  Sparkles,
  ClipboardList,
  Users,
  MessageCircle,
  Clock,
  Zap,
  Timer,
  DollarSign,
  AlertTriangle,
  Package,
  ShieldCheck,
  CreditCard,
} from "lucide-react";
import Link from "next/link";

// Mirrors backend/src/services/pricing.service.ts (estimatePricing):
//   price = base + estimatedTimeMinutes*1.2 + severityScore*20
//   price = round(price * urgencyMultiplier)
//   range shown to the customer = [price*0.9, price*1.15]
// "Low" scenario below = a quick 30min job, low severity, flexible timing.
// "High" scenario = a lengthy 4hr job, high severity, needed urgently now -
// i.e. the realistic ends of what that category actually estimates at.
const CATEGORIES = [
  { key: "plumbing", label: "Plumbing", icon: <Wrench size={22} />, base: 120, color: "blue", example: "Tap washer swap to a full re-pipe" },
  { key: "electrical", label: "Electrical", icon: <Bolt size={22} />, base: 140, color: "amber", example: "Power point fix to a switchboard upgrade" },
  { key: "handyman", label: "Handyman", icon: <Hammer size={22} />, base: 100, color: "orange", example: "Shelf mount to multi-room repairs" },
  { key: "hvac", label: "HVAC", icon: <Snowflake size={22} />, base: 150, color: "sky", example: "Filter service to a full system fault" },
  { key: "carpentry", label: "Carpentry", icon: <Ruler size={22} />, base: 115, color: "yellow", example: "Door hinge fix to custom built-ins" },
  { key: "painting", label: "Painting", icon: <Paintbrush size={22} />, base: 95, color: "rose", example: "Touch-up patch to a full room repaint" },
  { key: "roofing", label: "Roofing", icon: <Home size={22} />, base: 200, color: "slate", example: "Loose tile fix to storm damage repair" },
  { key: "landscaping", label: "Landscaping", icon: <TreeDeciduous size={22} />, base: 90, color: "emerald", example: "Lawn mow to a full yard clean-up" },
  { key: "cleaning", label: "Cleaning", icon: <Sparkles size={22} />, base: 80, color: "violet", example: "Single room to a full end-of-lease clean" },
  { key: "general", label: "General Repairs", icon: <Wrench size={22} />, base: 110, color: "teal", example: "Small fix to a multi-issue callout" },
];

const COLOR_CLASSES: Record<string, { bg: string; text: string }> = {
  blue: { bg: "bg-blue-50", text: "text-blue-600" },
  amber: { bg: "bg-amber-50", text: "text-amber-600" },
  orange: { bg: "bg-orange-50", text: "text-orange-600" },
  sky: { bg: "bg-sky-50", text: "text-sky-600" },
  yellow: { bg: "bg-yellow-50", text: "text-yellow-700" },
  rose: { bg: "bg-rose-50", text: "text-rose-600" },
  slate: { bg: "bg-slate-100", text: "text-slate-700" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600" },
  violet: { bg: "bg-violet-50", text: "text-violet-600" },
  teal: { bg: "bg-teal-50", text: "text-teal-600" },
};

// FixerLocal's cut of each job, deducted from the contractor's payout -
// customers are never charged more than the bid they accept.
const PLATFORM_COMMISSION_PERCENT = 15;

function priceRange(base: number): [number, number] {
  const low = base + 30 * 1.2 + 1 * 20; // quick job, low severity, flexible
  const high = (base + 240 * 1.2 + 3 * 20) * 1.5; // long job, high severity, urgent now
  const round5 = (n: number) => Math.round(n / 5) * 5;
  return [round5(low * 0.9), round5(high * 1.15)];
}

const URGENCY = [
  { label: "Flexible", multiplier: "1.0×", icon: <Clock size={18} />, desc: "No rush — schedule whenever suits you and your contractor." },
  { label: "Today", multiplier: "1.2×", icon: <Timer size={18} />, desc: "Needs sorting today — a modest premium for same-day availability." },
  { label: "Urgent now", multiplier: "1.5×", icon: <Zap size={18} />, desc: "Emergency response — priority dispatch to the nearest available pro." },
];

const STEPS = [
  { icon: <ClipboardList size={18} />, title: "Describe the job", body: "Get an instant AI-estimated range — free, no obligation." },
  { icon: <Users size={18} />, title: "Local pros bid", body: "Verified contractors in your area quote their own price and availability." },
  { icon: <MessageCircle size={18} />, title: "You pick the price", body: "Compare bids and choose — that's the price you actually pay." },
];

const FACTORS = [
  { icon: <DollarSign size={20} />, title: "Base call-out rate", body: "Every trade has its own starting rate — electrical and HVAC run higher than cleaning or landscaping, reflecting typical skill and equipment costs." },
  { icon: <Timer size={20} />, title: "Time on site", body: "Longer jobs cost more. Our AI estimates how long your issue will likely take from your description, and that time is priced in per minute." },
  { icon: <AlertTriangle size={20} />, title: "Severity", body: "Low, medium, or high complexity nudges the estimate — a blocked drain and active flooding aren't priced the same." },
  { icon: <Zap size={20} />, title: "Urgency", body: "Flexible timing keeps costs lowest. Needing it today or right now adds a premium for priority dispatch." },
  { icon: <Package size={20} />, title: "Parts & materials", body: "If your contractor needs extra materials mid-job, they itemize each part and you approve or decline it before it's added to your total." },
];

const FAQS = [
  { q: "Is there a booking fee or commission I pay?", a: `No. FixerLocal's ${PLATFORM_COMMISSION_PERCENT}% commission comes out of the contractor's payout, not your payment - you only ever pay the bid amount you accept.` },
  { q: "Do I pay anything before the work starts?", a: "No. There's no upfront payment or deposit. You're only charged once the job is marked complete and you confirm it." },
  { q: "What if I don't like any of the bids?", a: "You're not obligated to accept one. You can wait for more bids to come in, or cancel the job with no charge." },
  { q: "Can the price change after I pick a contractor?", a: "Only if extra parts or materials are needed — and even then, your contractor itemizes each one and you approve or reject it individually before it's added." },
  { q: "Is the AI estimate binding?", a: "No — it's a ballpark range to help you budget. The real price is whatever bid you choose to accept from a local contractor." },
];

export default function PricingPage() {
  return (
    <main className="min-h-screen bg-white pb-24 pt-32">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-bold mb-6"
          >
            Real Estimates, Real Bids
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-slate-950 mb-6">
            No subscriptions. <span className="text-emerald-600">Pay per job.</span>
          </h1>
          <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed">
            Describe your issue and get an instant AI price range, then local pros bid for the work.
            You only pay once the job's done — at the price you agreed to.
          </p>
        </div>

        {/* Our Fee - answers "what's the commission / are there hidden fees" directly */}
        <div className="mb-20 p-8 md:p-10 rounded-[2rem] bg-emerald-50 border border-emerald-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-white text-emerald-600 flex items-center justify-center shadow-sm">
              <DollarSign size={22} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-950 mb-2">Our fee, in plain English</h2>
              <p className="text-slate-600 leading-relaxed max-w-3xl">
                You pay exactly the bid amount you accept — <strong>no booking fee, no service fee, no markup</strong> added
                on top. FixerLocal earns a <strong>{PLATFORM_COMMISSION_PERCENT}% commission</strong>, taken from the
                contractor's payout after the job is paid for. It never changes what you're charged.
              </p>
            </div>
          </div>
        </div>

        {/* Category pricing grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {CATEGORIES.map((cat, i) => {
            const colors = COLOR_CLASSES[cat.color];
            const [low, high] = priceRange(cat.base);
            return (
              <motion.div
                key={cat.key}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="p-6 rounded-3xl border border-slate-200 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-900/5 transition-all bg-white"
              >
                <div className={`w-12 h-12 rounded-2xl ${colors.bg} ${colors.text} flex items-center justify-center mb-4`}>
                  {cat.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{cat.label}</h3>
                <p className="text-xl font-black text-slate-950">A${low}–A${high}</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Typical range</p>
                <p className="text-xs text-slate-400 leading-relaxed">{cat.example}</p>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-sm text-slate-400 max-w-2xl mx-auto mb-24">
          Ranges span a quick, low-complexity job at flexible timing up to a lengthy, urgent one.
          Your exact estimate appears the moment you describe the job.
        </p>

        {/* What affects your price */}
        <div className="mb-24">
          <div className="text-center mb-10">
            <p className="text-emerald-600 font-bold tracking-widest uppercase text-xs mb-3">The Breakdown</p>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-950 tracking-tight">What actually affects your price.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {FACTORS.map((f) => (
              <div key={f.title} className="p-6 rounded-3xl bg-slate-50 border border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-emerald-600 flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Urgency multiplier */}
        <div className="mb-24">
          <div className="text-center mb-10">
            <p className="text-emerald-600 font-bold tracking-widest uppercase text-xs mb-3">Timing Affects Price</p>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-950 tracking-tight">How urgency changes your estimate.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {URGENCY.map((u) => (
              <div key={u.label} className="p-6 rounded-3xl bg-slate-50 border border-slate-100 flex flex-col items-start">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-700 flex items-center justify-center mb-4">
                  {u.icon}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="font-bold text-slate-900">{u.label}</h3>
                  <span className="text-emerald-600 font-black text-sm">{u.multiplier}</span>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How the final price is set */}
        <div className="mb-24 p-8 md:p-12 rounded-[2.5rem] bg-slate-950 text-white">
          <div className="text-center mb-12">
            <p className="text-emerald-400 font-bold tracking-widest uppercase text-xs mb-3">No Fixed Quotes</p>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">The estimate isn't the final price — bids are.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="flex flex-col items-start">
                <div className="w-10 h-10 rounded-xl bg-white/10 text-emerald-400 flex items-center justify-center mb-4">
                  {s.icon}
                </div>
                <h3 className="font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Payment & trust */}
        <div className="mb-24 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-8 rounded-3xl border border-slate-200 flex gap-5">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CreditCard size={22} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Pay after, not before</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                No deposit, no upfront charge. You confirm the job is done, then pay securely by card —
                only for the bid amount you accepted plus any parts you approved.
              </p>
            </div>
          </div>
          <div className="p-8 rounded-3xl border border-slate-200 flex gap-5">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <ShieldCheck size={22} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2">No surprise costs</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Every added part or material is itemized and needs your approval before it's charged —
                nothing gets added to your bill without you seeing it first.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-20 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-emerald-600 font-bold tracking-widest uppercase text-xs mb-3">Common Questions</p>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-950 tracking-tight">Pricing, answered.</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="p-6 rounded-2xl border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-2">{f.q}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            href="/booking"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-emerald-600 text-white font-black hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-100 active:scale-[0.98]"
          >
            Get your estimate
            <ArrowRight size={18} />
          </Link>
          <div className="mt-8">
            <Link href="/" className="inline-flex items-center gap-2 text-slate-400 font-bold hover:text-slate-600 transition-colors text-sm">
              <ArrowLeft size={16} />
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
